/* eslint-disable no-unused-expressions */
/* eslint-disable react/destructuring-assignment */
import React, { FunctionComponent, useState, useEffect } from 'react';
import { connect, MapStateToProps, useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Accordion, Placement, AccordionPanel, Colors, AnalyticsProps, AnalyticsPage } from '@inplayer-org/inplayer-ui';
import { MdInfoOutline } from 'react-icons/md';
import styled from 'styled-components';
import { transparentize } from 'polished';

// utils
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import snakeCase from 'lodash/snakeCase';

// actions
import {
  loadItemDetails,
  // content
  loadItemAccessTypes,
  loadAllAccessFees,
  // types
  ItemAccessTypes,
  ItemType,
  setSelectedSectionIndex,
  setShouldCloseSection,
  setShouldClosePanel,
  // templates
  loadTemplateDetails,
} from 'ducks/merchantDucks/items/actions';
import {
  // Restrictions
  loadCountrySets,
  loadAssetCountrySet,
  loadAssetDomains,
  loadAssetAgeRestriction,
} from 'ducks/merchantDucks/restrictions/actions';
import {
  // in-app integration keys
  loadGoogleInAppKeys,
  loadAppleInAppKeys,
  loadAmazonInAppKeys,
  loadRokuInAppKeys,
} from 'ducks/merchantDucks/inAppIntegrations/actions';
// eventbrite keys
import { loadExternalAccount } from 'ducks/merchantDucks/externalAccount/actions';
import {
  // sso in embed code
  loadSessionUser,
} from 'ducks/merchantDucks/user/actions';
import {
  // modal
  openModal,
} from 'ducks/merchantDucks/modal/actions';

// selector
import { getFeatures } from 'ducks/merchantDucks/user/selectors';
import {
  getSelectedTemplateFee,
  getSelectedTemplateFeeId,
  getTemplateDetails,
  getTemplateRestrictionsInOriginalFormat,
  getItemLiveLikeResources,
  getIsPlanSwitchEnabled,
  getTotalSubscriptions,
} from 'ducks/merchantDucks/items/selectors';

// types
import { AccessControlType } from 'ducks/merchantDucks/accesses/actions';
import InAppIntegrationsState from 'ducks/merchantDucks/inAppIntegrations/types';
import RootState from 'ducks/RootState';
import { ItemDetails, PreviewMetadataNames } from 'ducks/merchantDucks/items/types';
import RestrictionsState from 'ducks/merchantDucks/restrictions/types';
import ExternalAccountState from 'ducks/merchantDucks/externalAccount/types';
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import { DefaultBrandingSet } from 'ducks/merchantDucks/branding/types';
import { SectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';
import { EventType } from 'pages/MerchantPages/Assets/InitialCreate/ContentType';
import { AccessControlTypeNames } from 'pages/MerchantPages/Assets/assetTypes';
import { FeatureNames } from 'ducks/merchantDucks/user/types';

//  hooks
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';
import useSharedObject from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedObject';
import useSharedState from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedState';

// components
import Metadata from 'pages/MerchantPages/Assets/Metadata';
import AssetContent from 'pages/MerchantPages/Assets/Content/containers/AssetContent';
import AssetsPriceContainer from 'pages/MerchantPages/Assets/Price/PriceContainer';
import CodesContainer from 'pages/MerchantPages/Assets/Codes/containers/CodesContainer';
import AssetPreviewForm from 'pages/MerchantPages/Assets/Preview';
import AssetRestrictionsForm from 'pages/MerchantPages/Assets/Restrictions/components/forms/AssetRestrictionsForm';
import PreviewTemplates from 'pages/MerchantPages/Assets/PreviewTemplates';
import EventbriteContainer from 'pages/MerchantPages/Assets/components/Eventbrite/EventbriteContainer';
import Donations from 'pages/MerchantPages/Assets/components/Donations/Donations';
import CodeAuth from 'pages/MerchantPages/Assets/components/CodeAuth';
import DataCaptureNoAuth from 'pages/MerchantPages/Assets/components/DataCaptureNoAuth';
import ConfirmModal from 'components/Modals/ConfirmModal';
import AssetContentDetails from 'pages/MerchantPages/Assets/ContentDetails/containers/AssetContentDetails';
import LiveLikeContainer from 'pages/MerchantPages/Assets/components/LiveLike/LiveLikeContainer';
import GiftDetails from 'pages/MerchantPages/Assets/GiftDetails';
import Loader from 'components/Loader';
import AssetNavbar from 'pages/MerchantPages/Assets/containers/EditAsset/sections/AssetNavbar';
import AssetPreview from 'pages/MerchantPages/Assets/containers/EditAsset/sections/AssetPreview';
import { AssetDividedWrapper } from 'components/SharedStyledElements';

export const AccordionWrapper = styled.div`
  box-shadow: 0 10px 20px ${transparentize(0.79, Colors.fontDarkGray)},
    0 6px 6px ${transparentize(0.83, Colors.fontDarkGray)};
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
`;

const PulseMdInfoOutline = styled(MdInfoOutline)<AnalyticsProps>`
  animation: pulse 2s infinite;
  border-radius: 50%;
  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 ${transparentize(0.3, Colors.orange)};
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px ${transparentize(0.7, Colors.orange)};
    }
    100% {
      box-shadow: 0 0 0 0 ${transparentize(0.7, Colors.orange)};
    }
  }
`;

interface StateProps {
  tenantUuid: string;
  assetDetails: ItemDetails;
  restrictions: RestrictionsState;
  accessTypes: ItemAccessTypes;
  selectedFeeId: number;
  selectedFee: any;
  inAppIntegrations: InAppIntegrationsState;
  concurrentSessions: number;
  subdomainName: string;
  isConcurrentSessionsFetching: boolean;
  externalAccount: ExternalAccountState;
  defaultBrandingSet: DefaultBrandingSet;
  isAccessFeesFetching: boolean;
  templateDetails: any;
  selectedTemplateFee: any;
  selectedTemplateFeeId: number;
  templateRestrictions: any;
  isPlanSwitchEnabled: boolean;
  totalSubscriptions: number;
}

type Props = StateProps;

interface PreviewFieldsState {
  title: string;
  description: string;
  buttonLabel?: string;
}

export const DEFAULT_TEMPLATE_ID = null;

const EditAsset: FunctionComponent<Props> = ({
  tenantUuid,
  // Item
  assetDetails,
  // Sessions
  concurrentSessions,
  subdomainName,
  isConcurrentSessionsFetching,
  // Restrictions
  restrictions: {
    assetCountrySet: { id: assetCountrySetId },
    assetDomainsInputs: { inputs: assetDomainRestrictions },
    ageRestriction,
    geoCollection: countrySets,
    isAgeRestrictionEnabled,
  },
  // Price
  accessTypes,
  selectedFee,
  selectedFeeId,
  // integration keys
  inAppIntegrations,
  externalAccount,
  isAccessFeesFetching,
  // templates
  templateDetails,
  selectedTemplateFee,
  selectedTemplateFeeId,
  templateRestrictions,
  isPlanSwitchEnabled,
  totalSubscriptions,
}: Props) => {
  const { id: assetId } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const isEditTemplate = pathname.includes('templates');

  const details = isEditTemplate ? templateDetails : assetDetails;
  const {
    id,
    item_type: { name: itemType = '' } = {},
    accessFees = [],
    metadata,
    access_control_type: { id: accessControlTypeId },
    // in order to use the same Preview templates flow
    // we will be using the name: template_id
    // but in reality, from BE it is returned as preview_id (for asset template feature)
    template_id: templateId,
    event_type: eventType,
    event_details: eventDetails,
  } = details;

  let previewTitle = '';
  let previewDescription = '';
  let previewButtonLabel = '';

  if (isEditTemplate) {
    if (metadata.length) {
      previewTitle = metadata?.find((item: { name: string }) => item.name === PreviewMetadataNames.PREVIEW_TITLE)
        ?.value;
      previewDescription = metadata?.find(
        (item: { name: string }) => item.name === PreviewMetadataNames.PREVIEW_DESCRIPTION
      )?.value;
      previewButtonLabel = metadata?.find((item: { name: string }) => item.name === PreviewMetadataNames.PREVIEW_BUTTON)
        ?.value;
    }
  } else {
    const { preview_button_label, preview_description, preview_title } = metadata;
    previewTitle = preview_title;
    previewButtonLabel = preview_button_label;
    previewDescription = preview_description;
  }

  const [oldPreviewFields, setOldPreviewFields] = useState({
    preview_title: previewTitle,
    preview_description: previewDescription,
    preview_button_label: previewButtonLabel,
    paywall_cover_photo: '',
  });
  const [activeSectionIndex, setActiveSectionIndex] = useState(-1);
  const [contentCategory, setContentCategory] = useState('');
  const [isContentDetailsFormValid, setIsContentDetailsFormValid] = useState(false);
  const [isExternalAccountsLoaded, setIsExternalAccountsLoaded] = useState(false);

  const [previewFields, setPreviewFields] = useSharedObject('previewFields', {
    title: previewTitle,
    description: previewDescription,
    buttonLabel: previewButtonLabel,
  });
  const [previewIndex, setPreviewIndex] = useSharedState('previewIndex', 1);
  const [previewTemplateId, setPreviewTemplateId] = useSharedState<number | null>(
    'previewTemplateId',
    DEFAULT_TEMPLATE_ID
  );
  const [isAnalyticsView] = useSharedBoolean('isAnalyticsView');
  const [, setUseDefaultBrandingTheme] = useSharedBoolean('useDefaultBrandingTheme');
  const [accordionHidden] = useSharedBoolean('accordionHidden');
  const [, setIsPreviewTemplateOpen] = useSharedBoolean('isPreviewTemplateOpen');
  const [, setUseDefaultTemplateId] = useSharedBoolean('useDefaultTemplateId');

  const isCodePassAsset = accessControlTypeId === AccessControlType.CODEAUTH;
  const isCodeOnly = accessControlTypeId === AccessControlType.CODEONLY;
  const isDataCaptureNoAuthAsset = accessControlTypeId === AccessControlType.DATACAPTURE;
  const isPaidAsset = accessControlTypeId === AccessControlType.PAID;
  const isFreemiumAsset = accessControlTypeId === AccessControlType.DATA;

  const shouldClose = useSelector((state: RootState) => state.items.itemDetails.shouldCloseSection);

  const isGiftsFeatureEnabled = useSelector(getFeatures).find((x) => x.name === FeatureNames.GIFTS)?.enabled;
  const isLiveLikeFeatureEnabled = useSelector(getFeatures).find((feature) => feature.name === FeatureNames.LIVELIKE)
    ?.enabled;

  const itemLiveLikeResources = useSelector(getItemLiveLikeResources);

  const loadInAppKeys = () => {
    if (!inAppIntegrations.isFetching) {
      dispatch(loadGoogleInAppKeys());
      dispatch(loadAppleInAppKeys());
      dispatch(loadAmazonInAppKeys());
      dispatch(loadRokuInAppKeys());
    }
  };

  const loadThirdPartyIntegrationsKeys = async () => {
    if (!externalAccount.isFetching && !externalAccount.saveOvpError) {
      await Promise.all([dispatch(loadExternalAccount('eventbrite')), dispatch(loadExternalAccount('livelike'))]);
      setIsExternalAccountsLoaded(true);
    }
  };

  useEffect(() => {
    loadInAppKeys();
    loadThirdPartyIntegrationsKeys();
  }, [loadGoogleInAppKeys, loadAppleInAppKeys, loadAmazonInAppKeys, loadRokuInAppKeys, loadExternalAccount]);

  useEffect(() => {
    if (isEditTemplate) {
      dispatch(loadTemplateDetails(Number(assetId)));
    } else {
      dispatch(loadItemDetails(tenantUuid, Number(assetId)));
    }
    dispatch(loadCountrySets());
    dispatch(loadAllAccessFees(Number(assetId)));
    dispatch(loadItemAccessTypes());
  }, [isEditTemplate, loadItemDetails, loadTemplateDetails, loadCountrySets, loadAllAccessFees, loadItemAccessTypes]);

  useEffect(() => {
    dispatch(loadAssetCountrySet(Number(assetId).toString()));
    dispatch(loadAssetDomains(Number(assetId).toString()));
    dispatch(loadAssetAgeRestriction(Number(assetId)));
  }, [loadAssetCountrySet, loadAssetDomains, loadAssetAgeRestriction]);

  useEffect(() => {
    if ((concurrentSessions || concurrentSessions === 0) && subdomainName === '' && !isConcurrentSessionsFetching) {
      // must check for removing infinite loop
      dispatch(loadSessionUser());
    }
  }, [concurrentSessions, subdomainName, loadSessionUser]);

  useEffect(() => {
    const { title, description, buttonLabel } = previewFields;
    if (
      !isEqual(buttonLabel, previewButtonLabel) ||
      !isEqual(description, previewDescription) ||
      !isEqual(title, previewTitle)
    ) {
      setPreviewFields({
        buttonLabel: previewButtonLabel,
        title: previewTitle,
        description: previewDescription,
      });
      setOldPreviewFields((prevState) => ({
        ...prevState,
        preview_button_label: previewButtonLabel,
        preview_title: previewTitle,
        preview_description: previewDescription,
      }));
    }
  }, [previewButtonLabel, previewDescription, previewTitle]);

  useEffect(() => {
    if (accessControlTypeId && previewIndex === 0) {
      setPreviewIndex(accessControlTypeId);
    }
  });

  useEffect(() => {
    setUseDefaultTemplateId(!templateId);
    if (templateId !== previewTemplateId) {
      setPreviewTemplateId(templateId);
    }
  }, [templateId]);

  const getDisabledFeaturesBeforeSection = (itemIndex: number) => {
    // all panel sections that depend on some value need to be added, in order to keep the same section index
    const featureDependency = {
      [SectionType.EventBriteEvent]: externalAccount.keys.eventbrite,
      [SectionType.GiftDetails]: isGiftsFeatureEnabled && isPaidAsset,
    };

    const filtered = Object.keys(featureDependency).filter(
      (key, index) => index < itemIndex && !featureDependency[key]
    );

    return filtered.length;
  };

  const handleActivePanelChanged = (index: number, label: string) => {
    if (index === -1) setPreviewIndex(AccessControlType.PAID);
    else if (index === 0 || index === 1 || label === 'Content details') {
      setPreviewIndex(AccessControlType.PAID); // this is the asset preview screen
    } else if (index === 2 && !isCodePassAsset && !isCodeOnly && !isDataCaptureNoAuthAsset) {
      setPreviewIndex(5); // 5 is the price preview screen
    } else if (index === -1) {
      setPreviewIndex(AccessControlType.PAID);
    } else {
      setPreviewIndex(accessControlTypeId);
    }
    setIsPreviewTemplateOpen(index === 1);
    setActiveSectionIndex(index + getDisabledFeaturesBeforeSection(index));
    if (index !== -1) dispatch(setShouldCloseSection(true));
  };

  const handleUpdateField = (oldFieldName: string, fieldName: string, value: string) => {
    setOldPreviewFields((prev) => ({
      ...prev,
      [oldFieldName]: value === '' ? '' : previewFields[fieldName],
    }));
    setPreviewFields({ [fieldName]: value });
  };

  const handleUpdatePreview = ({ title, buttonLabel, description }: Partial<PreviewFieldsState>) => {
    title && handleUpdateField('preview_title', 'title', title);
    buttonLabel && handleUpdateField('preview_button_label', 'buttonLabel', buttonLabel);
    description && handleUpdateField('preview_description', 'description', description);

    // Fix - delete last string in buttonLabel issue
    !buttonLabel && !description && !title && handleUpdateField('preview_button_label', 'buttonLabel', '');
  };

  const handlePreviewChange = (selectedTemplateId: number) => {
    setPreviewTemplateId(selectedTemplateId);
    setUseDefaultBrandingTheme(false);
    setUseDefaultTemplateId(false);
  };

  const handleSelectContentCategory = (category: string) => {
    setContentCategory(category);
  };

  const handleSetIsFormValid = (isValid: boolean) => {
    setIsContentDetailsFormValid(isValid);
  };

  const getPanelThreeTitle = () => {
    switch (accessControlTypeId) {
      case AccessControlType.CODEONLY:
        return AccessControlTypeNames.CODE_OPTIONS;
      case AccessControlType.CODEAUTH:
        return AccessControlTypeNames.ACCESS_VOUCHER_OPTIONS;
      case AccessControlType.DATACAPTURE:
        return AccessControlTypeNames.DATA_CAPTURE_FIELDS;
      default:
        return AccessControlTypeNames.PRICE_OPTIONS;
    }
  };

  const generatePanels = () => {
    const isDataAsset = accessControlTypeId === AccessControlType.DATA;
    const passcodeDisabled = isEditTemplate && accessControlTypeId === AccessControlType.CODEONLY;
    const panelThreeTtitle = getPanelThreeTitle();

    // Show pulse info icon in case of empty required fileds in content details panel header
    const showContentDetailsPulseInfoIcon =
      ((contentCategory !== '' && contentCategory === EventType.LIVE) || eventType === EventType.LIVE) &&
      eventDetails?.industry === '' &&
      !isContentDetailsFormValid;

    const renderNoAuthSection = {
      icon: <MdInfoOutline />,
      iconTooltip: {
        content: 'This is a Freemium asset. Customers get access by only making an account.\n No purchase is needed.',
        placement: 'left' as Placement,
      },
      renderContent: () => null,
    };

    const renderAuthSection = {
      renderContent: ({ closePanel }: any) => {
        switch (accessControlTypeId) {
          case AccessControlType.PAID:
            return (
              <AssetsPriceContainer
                accessTypes={accessTypes}
                countrySets={countrySets}
                accessFees={accessFees}
                inAppIntegrations={inAppIntegrations}
                selectedFee={selectedFee}
                accessControlTypeId={accessControlTypeId}
                selectedFeeId={selectedFeeId}
                closePanel={closePanel}
                itemId={Number(assetId)}
                itemType={ItemType.Asset}
                isAccessFeesFetching={isAccessFeesFetching}
                isEditTemplate={isEditTemplate}
                selectedTemplateFee={selectedTemplateFee}
                selectedTemplateFeeId={selectedTemplateFeeId}
                accordionLabel="Price options"
                isPlanSwitchEnabled={isPlanSwitchEnabled}
                totalSubscriptions={totalSubscriptions}
              />
            );
          case AccessControlType.CODEAUTH:
            return <CodeAuth />;
          case AccessControlType.CODEONLY:
            return <CodesContainer accordionLabel="Passcode options" closePanel={closePanel} />;
          case AccessControlType.DATACAPTURE:
            return <DataCaptureNoAuth />;
          default:
            return null;
        }
      },
    };

    const panels: AccordionPanel[] = [
      {
        label: 'Preview',
        renderContent: ({ closePanel }) => {
          return (
            <AssetPreviewForm
              handleUpdatePreview={handleUpdatePreview}
              closePanel={closePanel}
              isTemplate={isEditTemplate}
              oldPreviewFields={oldPreviewFields}
              accordionLabel="Preview"
            />
          );
        },
        tag: 'accordion_preview',
      },
      {
        label: 'Preview templates',
        renderContent: ({ closePanel }: any) => (
          <PreviewTemplates
            accordionLabel="Preview Templates"
            savedTemplateId={templateId}
            closePanel={closePanel}
            handlePreviewChange={handlePreviewChange}
            itemType={itemType}
          />
        ),
        tag: 'accordion_preview_templates',
      },
      {
        label: `${panelThreeTtitle}`,
        disabled: isDataAsset || passcodeDisabled,
        ...(isDataAsset ? renderNoAuthSection : renderAuthSection),
        tag: snakeCase(panelThreeTtitle),
      },
      ...(!isEditTemplate
        ? [
            {
              label: 'Content',
              renderContent: ({ closePanel }: any) => (
                <AssetContent accordionLabel="Accordion content" closePanel={closePanel} />
              ),
              tag: 'accordion_content',
            },
          ]
        : []),
      {
        label: 'Content details',
        renderContent: ({ closePanel }: { closePanel: () => void }) => (
          <AssetContentDetails
            closePanel={closePanel}
            handleSelectContentCategory={handleSelectContentCategory}
            handleSetIsFormValid={handleSetIsFormValid}
            isTemplate={isEditTemplate}
            accordionLabel="Content details"
          />
        ),
        tag: 'accordion_content_details',
        ...(showContentDetailsPulseInfoIcon
          ? {
              icon: <PulseMdInfoOutline tag="tooltip_content_details" color={Colors.orange} />,
              iconTooltip: {
                tag: `tooltip_content_details`,
                content: `Make sure your customers get the best support by filling in the details below.\nThis is for internal info purposes only and won't affect your asset settings.`,
                placement: 'left' as Placement,
              },
            }
          : null),
      },
      {
        label: 'Restrictions',
        renderContent: ({ closePanel }) => (
          <AssetRestrictionsForm
            assetId={Number(assetId)}
            countrySets={countrySets}
            restrictions={templateRestrictions}
            ageRestriction={isEditTemplate ? details.restrictions?.ageRestriction?.age : ageRestriction}
            assetCountrySetId={isEditTemplate ? details.restrictions?.assetCountrySet?.id : assetCountrySetId}
            assetDomainRestrictions={
              isEditTemplate ? details.restrictions?.assetDomainsInputs?.inputs : assetDomainRestrictions
            }
            isAgeRestrictionEnabled={isAgeRestrictionEnabled}
            isTemplate={isEditTemplate}
            closePanel={closePanel}
            accordionLabel="Restrictions"
          />
        ),
        tag: 'accordion_restrictions',
      },
      {
        label: 'Additional metadata',
        renderContent: ({ closePanel }) => {
          return (
            <Metadata
              metadata={metadata}
              closePanel={closePanel}
              itemType={itemType}
              accordionLabel="Additional Metadata"
            />
          );
        },
        tag: 'accordion_additional_metadata',
      },
    ];

    // check if eventbrite integration is made and show the additional panel
    const showEventbrite = externalAccount.keys.eventbrite && !isEditTemplate;
    showEventbrite &&
      panels.push({
        label: 'Relate an Eventbrite event',
        renderContent: ({ closePanel }) => (
          <EventbriteContainer
            closePanel={closePanel}
            itemType={itemType}
            assetMetadata={metadata}
            assetId={id}
            accordionLabel="Relate an eventbrite event"
          />
        ),
        tag: 'accordion_relate_an_eventbrite_event',
      });

    // To show the additional audience engagement widgets panel,
    // check if livelike feature is enabled, asset is paid or voucher
    // access only or freemium and livelike integration is made
    const isAuthAsset = isPaidAsset || isCodePassAsset || isFreemiumAsset;
    const showLiveLikePanel = isAuthAsset && isLiveLikeFeatureEnabled && externalAccount.keys.livelike;
    // Show pulse info icon in case of not set at least one widget
    // in audience engagement widgets panel header
    const enabledWidgets = mapValues(itemLiveLikeResources, (value) => value.length !== 0);
    const showAudienceEngagementPulseIcon = isEmpty(pickBy(enabledWidgets, (value) => value === true));

    showLiveLikePanel &&
      panels.push({
        label: 'Audience engagement widgets',
        renderContent: ({ closePanel }) => <LiveLikeContainer closePanel={closePanel} />,
        ...(showAudienceEngagementPulseIcon
          ? {
              icon: <PulseMdInfoOutline color={Colors.orange} />,
              iconTooltip: {
                content: `Make sure your customers get the best user experience\nby enabling some of the audience engagement widgets.`,
                placement: 'left' as Placement,
              },
            }
          : null),
        tag: 'accordion_audience_engagement_widgets',
      });

    const showGifts = isGiftsFeatureEnabled && isPaidAsset && !isEditTemplate;
    showGifts &&
      panels.push({
        label: 'Gift details',
        renderContent: ({ closePanel }) => <GiftDetails accordionLabel="Gift details" closePanel={closePanel} />,
        tag: 'accordion_gift_details',
      });

    const isAssetEligibleForDonation =
      accessControlTypeId !== AccessControlType.CODEONLY && accessControlTypeId !== AccessControlType.DATACAPTURE;
    const showDonations = isAssetEligibleForDonation && !isEditTemplate;
    showDonations &&
      panels.push({
        label: 'Donations',
        renderContent: ({ closePanel }) => <Donations closePanel={closePanel} />,
        tag: 'accordion_donations',
      });

    return (
      !accordionHidden &&
      !isAnalyticsView && (
        <AccordionWrapper>
          <Accordion
            onActivePanelChange={handleActivePanelChanged}
            isExtendable
            panels={panels}
            contentHeight="100vh"
            shouldClose={shouldClose}
            onRequestClose={() => dispatch(openModal(ModalNames.ConfirmCloseSection))}
          />
        </AccordionWrapper>
      )
    );
  };

  if (!isExternalAccountsLoaded) {
    return <Loader />;
  }

  const editSection = (
    <AnalyticsPage tag="edit" type="page">
      <AnalyticsPage tag={assetId} type="page">
        <AnalyticsPage tag={isAnalyticsView ? 'view_asset_analytics' : 'view_asset_details'} type="page">
          <AssetNavbar />
          <AssetDividedWrapper>
            <AssetPreview />
            {generatePanels()}
          </AssetDividedWrapper>
          <ConfirmModal
            modalName={ModalNames.ConfirmCloseSection}
            message="You have unsaved changes."
            handleConfirm={() => {
              dispatch(setSelectedSectionIndex(activeSectionIndex));
              dispatch(setShouldCloseSection(true));
            }}
            handleCloseModal={() => {
              dispatch(setShouldClosePanel(true));
            }}
            confirmText="Save"
            cancelText="Don't Save"
          />
        </AnalyticsPage>
      </AnalyticsPage>
    </AnalyticsPage>
  );

  if (isEditTemplate) {
    return (
      <AnalyticsPage tag="templates" type="page">
        {editSection}
      </AnalyticsPage>
    );
  }
  return editSection;
};

export const mapStateToProps: MapStateToProps<StateProps, {}, RootState> = (state) => ({
  tenantUuid: state.auth.user.tenant_uuid,
  assetDetails: state.items.itemDetails,
  restrictions: state.restrictions,
  accessTypes: state.items.accessTypes,
  selectedFeeId: state.items.itemDetails.selectedFeeId,
  selectedFee: state.items.itemDetails.accessFees.find((item) => item.id === state.items.itemDetails.selectedFeeId),
  inAppIntegrations: state.inAppIntegrations,
  concurrentSessions: state.auth.concurrent_sessions,
  subdomainName: state.auth.sub_domain_name,
  isConcurrentSessionsFetching: state.auth.isFetching,
  externalAccount: state.externalAccount,
  defaultBrandingSet: state.branding.defaultBrandingSet,
  isAccessFeesFetching: state.items.itemDetails.isAccessFeesFetching,
  templateDetails: getTemplateDetails(state),
  selectedTemplateFeeId: getSelectedTemplateFeeId(state),
  selectedTemplateFee: getSelectedTemplateFee(state),
  templateRestrictions: getTemplateRestrictionsInOriginalFormat(state),
  isPlanSwitchEnabled: getIsPlanSwitchEnabled(state),
  totalSubscriptions: getTotalSubscriptions(state),
});

export default connect(mapStateToProps, {})(EditAsset);
